import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import type { ListSort } from '../api/useSubmissions.ts';
import { Ikon } from '../status/Ikon.tsx';

/**
 * Rekkefølgen på listene i begge rom (DD-74, M4.12). Valget står i adressen som `?sort=nyest`
 * — aldri i `localStorage` — så det overlever en ny innlasting og kan deles, og en ny økt
 * starter i fristrekkefølgen. Uten parameteren gjelder «Frist først».
 */
export function useRekkefolge(): [ListSort, (sort: ListSort) => void] {
  const [params, setParams] = useSearchParams();
  const sort: ListSort = params.get('sort') === 'nyest' ? 'nyest' : 'frist';
  const sett = useCallback(
    (ny: ListSort) =>
      setParams(
        (forrige) => {
          const neste = new URLSearchParams(forrige);
          if (ny === 'nyest') neste.set('sort', 'nyest');
          else neste.delete('sort');
          return neste;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [sort, sett];
}

/** Lista sier rekkefølgen i `title` (A9) — aldri som prosa i toppen. */
export const sortertTittel = (sort: ListSort, frist: string) => (sort === 'nyest' ? 'Sortert: nyest først' : `Sortert: ${frist}`);

/** Segmentet «Frist først · Nyest først», samme form som sporfilteret. Valget er ikke et filter. */
export function RekkefolgeVelger({ sort, onVelg }: { sort: ListSort; onVelg: (sort: ListSort) => void }) {
  return (
    <div className="panel-filter-segment panel-rekkefolge" role="group" aria-label="Rekkefølge">
      {(
        [
          ['frist', 'Frist først'],
          ['nyest', 'Nyest først'],
        ] as const
      ).map(([verdi, navn]) => (
        <button key={verdi} type="button" className="panel-filter-valg" aria-pressed={sort === verdi} onClick={() => onVelg(verdi)} data-testid={`rekkefolge-${verdi}`}>
          {navn}
        </button>
      ))}
    </div>
  );
}

/**
 * Stripa over lista (DD-74): «↑ 3 nye innspill · Vis». Lista endres først ved trykket, så raden
 * brukeren sikter på aldri flytter seg. I «Frist først» står de nye nederst, så lenken bytter
 * til «Nyest først» og sier det før trykket. Live-regionen står alltid i DOM, så skjermleseren
 * hører endringen; tom er den uten flate.
 */
export function NyeStripe({ antall, flere, sort, entall, flertall, onVis }: { antall: number; flere: boolean; sort: ListSort; entall: string; flertall: string; onVis: () => void }) {
  return (
    <div className="panel-nye" role="status" aria-live="polite">
      {antall > 0 ? (
        <p className="panel-nye-linje" data-testid="nye-stripe">
          <Ikon navn="arrow-up-outline" />
          <span>
            {flere ? `${antall}+` : antall} {antall === 1 && !flere ? entall : flertall}
          </span>
          <span aria-hidden="true">·</span>
          <button type="button" className="panel-knapp-lenke" onClick={onVis}>
            {sort === 'nyest' ? 'Vis' : 'Vis nyeste'}
          </button>
        </p>
      ) : null}
    </div>
  );
}
